/**
 * StockEventRepository — the dashboard's window onto the stock ledger.
 *
 * Stock is an append-only list of events; on-hand is derived from them. The
 * dashboard never writes a count directly — it appends an event and lets the
 * cloud recompute, exactly as a till does. That is what keeps the dashboard,
 * every terminal and the ledger telling the same story.
 *
 *   POST /stock/events/sync   append events
 *   GET  /stock/events        history (per product, or from a watermark)
 *   POST /stock/rebuild       admin: replay the ledger into the cache
 *
 * For a product tracked per variant every event names the variant, as
 * `optionId` — the field's name predates variants and is kept for
 * compatibility; it always holds a variant id (GRN/variants spec 3.3).
 *
 * See docs/feature-stock-management-implementation.md §7.1.
 */
import {posApiRequest} from './posApi';

/** Who the ledger records these events as. Not a real device. */
const DASHBOARD_DEVICE = 'dashboard';
const DASHBOARD_SOURCE = 'dashboard';

export type StockEventType =
  | 'sale'
  | 'refund'
  | 'waste'
  | 'purchase'
  | 'prep_set'
  | 'count_adjust'
  | 'override_sale'
  | 'purchase_reversal';

export interface StockEvent {
  eventId: string;
  /** The dish. Empty on a bare ingredient movement — rice belongs to no dish. */
  productId: string;
  /** Set when the event moves a raw material instead of a finished item. */
  stockId?: string | null;
  /** The goods-received document behind a purchase, when there was one. */
  intakeId?: string | null;
  parentEventId?: string | null;
  /** The variant id, for a product tracked per variant. */
  optionId?: string | null;
  type: StockEventType;
  delta: number;
  absolute?: number | null;
  reason?: string | null;
  orderId?: string | null;
  batchId?: string | null;
  deviceId?: string;
  userId?: string | null;
  source?: string;
  createdAt: string;
}

export interface SyncResult {
  syncedCount: number;
  skippedCount: number;
  failedCount: number;
  results: {eventId: string; result: string; reason?: string}[];
}

/** Types that carry an absolute quantity rather than a signed delta. */
const ABSOLUTE_TYPES: StockEventType[] = ['prep_set', 'count_adjust'];

/**
 * A manual event needs a fresh id every time: a second waste of one unit is
 * a second real event, not a duplicate of the first. (Order-derived events
 * are the opposite — their ids are deterministic so a replay is a no-op.)
 */
function newEventId(): string {
  const g: any = globalThis as any;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return `dash-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

const StockEventRepository = {
  /**
   * Append one manual event. `quantity` is always positive — the type
   * decides the sign and whether it lands in `delta` or `absolute`.
   */
  async recordEvent(
    shopId: string,
    input: {
      /** One of these two. An ingredient event names a stockId. */
      productId?: string;
      stockId?: string;
      /** The variant, for a product tracked per variant. Sent as optionId. */
      variantId?: string | null;
      type: StockEventType;
      quantity: number;
      reason?: string | null;
      userId?: string | null;
    },
  ): Promise<SyncResult> {
    const absolute = ABSOLUTE_TYPES.includes(input.type);
    const qty = Math.abs(Number(input.quantity));
    const positive = input.type === 'purchase' || input.type === 'refund';

    const event: StockEvent = {
      eventId: newEventId(),
      // The cloud accepts productId OR stockId. An ingredient event sends an
      // empty productId because no dish caused it.
      productId: input.productId ?? '',
      ...(input.stockId ? {stockId: input.stockId} : {}),
      ...(input.variantId ? {optionId: input.variantId} : {}),
      type: input.type,
      delta: absolute ? 0 : positive ? qty : -qty,
      absolute: absolute ? qty : null,
      reason: input.reason ?? null,
      deviceId: DASHBOARD_DEVICE,
      userId: input.userId ?? null,
      source: DASHBOARD_SOURCE,
      createdAt: new Date().toISOString(),
    };

    return posApiRequest('/stock/events/sync', {
      method: 'POST',
      body: JSON.stringify({
        merchantId: shopId,
        deviceId: DASHBOARD_DEVICE,
        source: DASHBOARD_SOURCE,
        events: [event],
      }),
    });
  },

  /** One product's history, newest first. */
  async getProductHistory(
    shopId: string,
    productId: string,
    limit = 50,
  ): Promise<StockEvent[]> {
    const data = await posApiRequest(
      `/stock/events?merchantId=${encodeURIComponent(shopId)}` +
        `&productId=${encodeURIComponent(productId)}&limit=${limit}`,
    );
    return data.events ?? [];
  },

  /** One variant's history, newest first. */
  async getVariantHistory(
    shopId: string,
    productId: string,
    variantId: string,
    limit = 50,
  ): Promise<StockEvent[]> {
    const data = await posApiRequest(
      `/stock/events?merchantId=${encodeURIComponent(shopId)}` +
        `&productId=${encodeURIComponent(productId)}` +
        `&variantId=${encodeURIComponent(variantId)}&limit=${limit}`,
    );
    return data.events ?? [];
  },

  /** One ingredient's history, newest first. */
  async getIngredientHistory(
    shopId: string,
    stockId: string,
    limit = 50,
  ): Promise<StockEvent[]> {
    const data = await posApiRequest(
      `/stock/events?merchantId=${encodeURIComponent(shopId)}` +
        `&stockId=${encodeURIComponent(stockId)}&limit=${limit}`,
    );
    return data.events ?? [];
  },

  /** Everything after a watermark, oldest first. */
  async getEvents(
    shopId: string,
    since = '1970-01-01T00:00:00.000Z',
    limit = 500,
  ): Promise<{events: StockEvent[]; nextSince: string}> {
    const data = await posApiRequest(
      `/stock/events?merchantId=${encodeURIComponent(shopId)}` +
        `&since=${encodeURIComponent(since)}&limit=${limit}`,
    );
    return {events: data.events ?? [], nextSince: data.nextSince ?? since};
  },

  /** Replay the ledger into the derived cache. Admin recovery, not routine. */
  async rebuild(shopId: string, productId?: string, stockId?: string): Promise<any> {
    return posApiRequest('/stock/rebuild', {
      method: 'POST',
      body: JSON.stringify({
        merchantId: shopId,
        ...(productId ? {productId} : {}),
        ...(stockId ? {stockId} : {}),
      }),
    });
  },
};

export default StockEventRepository;
